import { useMutation } from "@tanstack/react-query";
import { UTxO } from "lucid-cardano";
import { getTasteTestSdk } from "../../gql/client/tasteTestSdk";
import { TActionType } from "../../pages/TasteTestDetail/context";
import { useAppContext } from "../../stores";

export interface IUseReservedUtxosParams {
  tasteTestPolicyId?: string;
  owner: string;
  type: TActionType;
  handleError: () => void;
}

/**
 * Custom hook for managing Taste Test UTXO selection based on the given parameters.
 * It leverages the application's context to select UTXOs based on the user's specific
 * action, such as deposit, update, and withdrawal. The hook handles SDK interactions safely
 * by checking the availability of necessary parameters and the SDK itself before proceeding with any operations.
 *
 * This streamlines the utxo selection process for user's wanting to inject their deposit
 * into the linked-list of utxos. It is primarily for usability purposes (so the client doesn't have to ping
 * Blockfrost services), but it does fallback gracefully if a UTXO can't be found.
 *
 * @param {IUseReservedUtxosParams} params - The parameters required for fetching reserved UTXOs.
 * @param {string} params.tasteTestPolicyId - The ID associated with the taste test policy, used to identify specific UTXOs.
 * @param {string} params.owner - The owner's identifier for whom the UTXOs are reserved.
 * @param {("deposit"|"update"|"withdraw")} params.type - The type of operation to perform with the UTXOs (deposit, update, withdraw).
 * @param {Function} params.handleError - A callback function to handle errors that may occur during UTXO operations.
 *
 * @returns {Object} An object containing a single method `getReservedUtxos`, which when called, will perform
 * the UTXO operation based on the provided parameters and return the reserved UTXOs or undefined if the operation cannot be completed.
 *
 * @example
 * ```ts
 * const { getReservedUtxos } = useReservedUtxos({ tasteTestPolicyId, owner, type, handleError });
 * getReservedUtxos().then(utxos => {
 *   // Handle the reserved UTXOs
 * });
 * ```
 */
export const useReservedUtxos = ({
  tasteTestPolicyId,
  owner,
  type,
  handleError,
}: IUseReservedUtxosParams) => {
  const {
    state: { sdk },
  } = useAppContext();
  const { mutateAsync } = useMutation(
    [tasteTestPolicyId, owner, type],
    async () => {
      if (!tasteTestPolicyId) {
        return;
      }

      if (!sdk) {
        throw new Error("Attempted to interact with the SDK before it was available.");
      }

      const { getDepositUtxo, getUpdateUtxo, getWithdrawalUtxo } = await getTasteTestSdk();

      const lucid = sdk.builder().lucid;

      let utxos: UTxO[] | undefined;
      switch (type) {
        case "deposit": {
          const res = await getDepositUtxo({
            owner,
            tasteTest: tasteTestPolicyId,
          });
          utxos = await lucid.utxosByOutRef([
            {
              txHash: res.reserveForDeposit.txHash,
              outputIndex: res.reserveForDeposit.index,
            },
          ]);
          break;
        }
        case "update":
        case "claim": {
          const res = await getUpdateUtxo({
            owner,
            tasteTest: tasteTestPolicyId,
          });
          utxos = await lucid.utxosByOutRef([
            {
              txHash: res.reserveForUpdate.txHash,
              outputIndex: res.reserveForUpdate.index,
            },
          ]);
          break;
        }
        case "withdraw": {
          const res = await getWithdrawalUtxo({
            owner,
            tasteTest: tasteTestPolicyId,
          });
          utxos = await lucid.utxosByOutRef(
            res.reserveForWithdrawal.map(({ txHash, index }) => ({
              txHash: txHash,
              outputIndex: index,
            }))
          );
          break;
        }
      }

      return utxos;
    },
    {
      onError: () => {
        handleError();
      },
      retry: 5,
      retryDelay: 1000 * 2,
    }
  );

  return {
    getReservedUtxos: mutateAsync,
  };
};
